/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useFetchTagSettings = (params) => {
    const [tagSettings, setTagSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/tagSettings', {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.w('No tag settings found.', response);
                } else {
                    const data = Response.getData(response);

                    setTagSettings(data);
                }
            } catch (error) {
                debug.e('Error getting tag settings.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData, params]);

    return {
        loading,
        refetchData,
        tagSettings
    };
};

/**
 * A React hook to load tag settings as needed to the API.
 * @returns {Object} An object containing the get function and a loading state.
 */
export const useFetchTagSettingsAsync = () => {
    const [loading, setLoading] = useState(true);

    const getTagSettings = useCallback(async (params) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        try {
            setLoading(true);
            const response = await axios.get('/tagSettings', {
                cancelToken: source.token,
                params
            });
            if (!Response.hasData(response)) {
                debug.w('No tag settings found.', response);
            }

            const data = Response.getData(response);

            setLoading(false);
            return data;
        } catch (err) {
            debug.e('Error getting tag settings.', err);

            setLoading(false);
            return [];
        }
    }, []);

    return {
        getTagSettings,
        loading
    };
};

export const usePostTagSettings = () => {
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const postTagSettings = useCallback(
        async ({
            name,
            description,
            color,
            globalTagId,
            platformTenantId,
            shouldNotify = true
        }) => {
            const params = {
                color,
                description,
                globalTagId,
                platformTenantId
            };

            if (globalTagId) {
                params.globalTagId = globalTagId;
            }
            if (!globalTagId && name) {
                params.globalTag = { name };
            }

            try {
                if (!params.globalTagId && !params.globalTag) {
                    throw new Error('No globalTagId or globalTag provided');
                }

                setSaving(true);
                const response = await axios.post('/tagSettings', params);
                if (!Response.hasData(response)) {
                    const msg = 'No TagSettings Response';

                    throw new Error(msg);
                }

                if (shouldNotify) {
                    Notify.s(t('EditTagsModal.createTagSettings.success'));
                }
                setSaving(false);

                return true;
            } catch (err) {
                debug.e('Unable to add TagSettings', err);
                if (shouldNotify) {
                    Notify.e(t('EditTagsModal.createTagSettings.error'));
                }
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { postTagSettings, saving };
};
